import { showToast } from './Toast.js';
import { clearSession } from '../session.js';
import type { Business } from '../../core/app-store.js';
import { createHoldRecord, liftHold, getHoldRecord, ALL_REQUIREMENTS } from './AccountHold.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

// Estimated monthly value per business by industry
const ESTIMATED_VALUE: Record<string, { revenue: number; saving: number }> = {
  construction: { revenue: 149, saving: 2800 },
  restaurant: { revenue: 99, saving: 1400 },
  general: { revenue: 79, saving: 800 },
};

export async function renderAdminPanel(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title"><i class="bi bi-shield-shaded"></i> Admin Console</h1>
          <p class="page-subtitle">Preuve AI platform management — business approvals, analytics, and controls</p>
        </div>
        <div style="display:flex;gap:8px">
          <button class="btn btn-ghost btn-sm" id="invite-btn">
            <i class="bi bi-envelope-plus"></i> Invite Business
          </button>
          <button class="btn btn-ghost btn-sm" id="admin-logout">
            <i class="bi bi-box-arrow-left"></i> Sign Out
          </button>
        </div>
      </div>
    </div>

    <!-- Stats row -->
    <div class="stats-grid" style="margin-bottom:24px">
      <div class="stat-card">
        <div class="stat-value" id="stat-pending" style="color:var(--warning)">—</div>
        <div class="stat-label"><i class="bi bi-hourglass-split"></i> Pending Review</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" id="stat-approved" style="color:var(--success)">—</div>
        <div class="stat-label"><i class="bi bi-check-circle-fill"></i> Active Businesses</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" id="stat-mrr" style="color:var(--brand-gold)">—</div>
        <div class="stat-label"><i class="bi bi-graph-up-arrow"></i> Est. MRR</div>
      </div>
      <div class="stat-card">
        <div class="stat-value" id="stat-savings" style="color:#3b82f6">—</div>
        <div class="stat-label"><i class="bi bi-piggy-bank-fill"></i> Client Savings / Mo</div>
      </div>
    </div>

    <!-- Revenue breakdown chart area -->
    <div class="card" style="margin-bottom:20px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <div style="font-size:14px;font-weight:700"><i class="bi bi-bar-chart-fill" style="color:var(--brand-gold)"></i> Revenue by Industry</div>
        <div style="font-size:11px;color:var(--text-muted)">Based on active businesses</div>
      </div>
      <div id="revenue-chart" style="display:flex;flex-direction:column;gap:10px"></div>
    </div>

    <!-- Invite Modal -->
    <div id="invite-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:500px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:4px"><i class="bi bi-envelope-plus"></i> Invite a Business</h3>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px">Send a personalized invitation email via SendGrid.</p>
        <div class="form-row-2">
          <div>
            <label>Contact Name *</label>
            <input type="text" id="inv-name" placeholder="John Smith" />
          </div>
          <div>
            <label>Email *</label>
            <input type="email" id="inv-email" placeholder="john@business.com" />
          </div>
        </div>
        <div class="form-row-2">
          <div>
            <label>Business Name</label>
            <input type="text" id="inv-biz" placeholder="Smith Construction LLC" />
          </div>
          <div>
            <label>Industry</label>
            <select id="inv-industry">
              <option value="construction">🏗 Construction</option>
              <option value="restaurant">🍽 Restaurant</option>
              <option value="general">💼 General Business</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <label>Personal Message (optional)</label>
          <textarea id="inv-message" placeholder="We think Preuve AI would be a great fit for your business…" style="min-height:80px"></textarea>
        </div>
        <div style="background:var(--bg-tertiary);border-radius:var(--radius-sm);padding:10px;margin-bottom:12px;font-size:12px;color:var(--text-muted)">
          <i class="bi bi-info-circle"></i> Sent via SendGrid — configure <code>SENDGRID_API_KEY</code> in your environment.
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end">
          <button class="btn btn-ghost" id="close-invite-modal">Cancel</button>
          <button class="btn btn-gold" id="send-invite-btn">
            <i class="bi bi-send"></i> Send Invitation
          </button>
        </div>
      </div>
    </div>

    <!-- Place on Hold Modal -->
    <div id="pause-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1000;align-items:flex-start;justify-content:center;padding:40px 16px;overflow-y:auto">
      <div class="card" style="width:600px;max-width:100%">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:4px;color:var(--warning)">
          <i class="bi bi-shield-exclamation"></i> Place Account on Hold
        </h3>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:20px">
          The business will see a full-screen hold resolution page with your reason and the requirements you select below.
          They can respond, upload documents, and make commitments to lift the hold.
        </p>

        <input type="hidden" id="pause-biz-id" />
        <input type="hidden" id="pause-biz-name" />

        <div class="form-row" style="margin-bottom:14px">
          <label>Reason for Hold <span style="color:var(--danger)">*</span></label>
          <textarea id="pause-reason" placeholder="Outstanding balance, missing compliance documents, verification required, etc."
            style="min-height:80px"></textarea>
        </div>

        <div class="form-row" style="margin-bottom:14px">
          <label>Response Deadline <span style="font-size:11px;color:var(--text-muted)">(optional)</span></label>
          <input type="date" id="pause-deadline" />
        </div>

        <div class="form-row" style="margin-bottom:14px">
          <label>Admin Note <span style="font-size:11px;color:var(--text-muted)">(internal, not shown to business)</span></label>
          <input type="text" id="pause-admin-note" placeholder="e.g. Flagged by compliance review on 2026-05-26" />
        </div>

        <div style="margin-bottom:16px">
          <label style="font-size:13px;font-weight:600;display:block;margin-bottom:8px">
            Required From Business
            <span style="font-size:11px;color:var(--text-muted);font-weight:400"> — select what you're requiring</span>
          </label>
          <div class="hold-modal-req-list">
            ${ALL_REQUIREMENTS.map(r => `
              <label class="hold-modal-req-item">
                <input type="checkbox" class="hold-modal-req-cb" value="${escHtml(r.key)}" />
                <i class="bi ${escHtml(r.icon)}" style="color:var(--brand-gold);font-size:16px;flex-shrink:0"></i>
                <span>${escHtml(r.label)}</span>
              </label>`).join('')}
          </div>
        </div>

        <div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap">
          <button class="btn btn-ghost" id="close-pause-modal">Cancel</button>
          <button class="btn" style="background:var(--warning);color:#000;font-weight:700" id="confirm-pause-btn">
            <i class="bi bi-shield-exclamation"></i> Place on Hold
          </button>
        </div>
      </div>
    </div>

    <!-- View Hold Response Modal -->
    <div id="hold-response-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1001;align-items:flex-start;justify-content:center;padding:40px 16px;overflow-y:auto">
      <div class="card" style="width:600px;max-width:100%">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
          <h3 style="font-size:15px;font-weight:700" id="hold-resp-title">Hold Response</h3>
          <button class="btn btn-ghost btn-sm" id="close-hold-response-modal"><i class="bi bi-x-lg"></i></button>
        </div>
        <div id="hold-resp-content"></div>
      </div>
    </div>

    <!-- Business Detail Modal -->
    <div id="detail-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:580px;max-height:80vh;overflow-y:auto">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px">
          <h3 style="font-size:15px;font-weight:700" id="detail-title">Business Details</h3>
          <button class="btn btn-ghost btn-sm" id="close-detail-modal"><i class="bi bi-x-lg"></i></button>
        </div>
        <div id="detail-content"></div>
      </div>
    </div>

    <!-- Business List -->
    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;flex-wrap:wrap;gap:10px">
        <h2 style="font-size:14px;font-weight:700"><i class="bi bi-buildings"></i> All Businesses</h2>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          <input type="text" id="biz-search" placeholder="Search businesses…" style="width:180px;font-size:12px;padding:6px 10px" />
          <select id="filter-status" style="width:140px;padding:6px 10px;font-size:12px">
            <option value="all">All Statuses</option>
            <option value="pending" selected>Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="paused">Paused</option>
          </select>
          <select id="filter-industry" style="width:140px;padding:6px 10px;font-size:12px">
            <option value="all">All Industries</option>
            <option value="construction">Construction</option>
            <option value="restaurant">Restaurant</option>
            <option value="general">General</option>
          </select>
        </div>
      </div>
      <div id="business-list"><div class="empty-state"><div class="spinner"></div></div></div>
    </div>
  `;

  document.getElementById('admin-logout')!.addEventListener('click', () => { clearSession(); navigate('login'); });
  document.getElementById('filter-status')!.addEventListener('change', () => loadBusinesses(navigate));
  document.getElementById('filter-industry')!.addEventListener('change', () => loadBusinesses(navigate));
  document.getElementById('biz-search')!.addEventListener('input', () => loadBusinesses(navigate));

  // Invite modal
  document.getElementById('invite-btn')!.addEventListener('click', () => {
    document.getElementById('invite-modal')!.style.display = 'flex';
  });
  document.getElementById('close-invite-modal')!.addEventListener('click', () => {
    document.getElementById('invite-modal')!.style.display = 'none';
  });
  document.getElementById('send-invite-btn')!.addEventListener('click', () => sendInvitation());

  // Hold modal
  document.getElementById('close-pause-modal')!.addEventListener('click', () => {
    document.getElementById('pause-modal')!.style.display = 'none';
  });
  document.getElementById('confirm-pause-btn')!.addEventListener('click', async () => {
    const id         = (document.getElementById('pause-biz-id') as HTMLInputElement).value;
    const bizName    = (document.getElementById('pause-biz-name') as HTMLInputElement).value;
    const reason     = (document.getElementById('pause-reason') as HTMLTextAreaElement).value.trim();
    const deadline   = (document.getElementById('pause-deadline') as HTMLInputElement).value || undefined;
    const adminNote  = (document.getElementById('pause-admin-note') as HTMLInputElement).value.trim() || undefined;
    const reqBoxes   = document.querySelectorAll<HTMLInputElement>('.hold-modal-req-cb:checked');
    const requirements = Array.from(reqBoxes).map(cb => cb.value);

    if (!reason) { showToast('Please enter a reason for the hold', 'error'); return; }

    // Create hold record in window store
    createHoldRecord(id, bizName, reason, requirements, deadline, adminNote);

    try {
      await getAPI().pauseBusiness(id, reason || undefined);
    } catch { /* preview mode */ }

    showToast(`Account placed on hold — business will see hold resolution page on next login`, 'info');
    document.getElementById('pause-modal')!.style.display = 'none';
    await loadBusinesses(navigate);
  });

  // View hold response modal
  document.getElementById('close-hold-response-modal')!.addEventListener('click', () => {
    document.getElementById('hold-response-modal')!.style.display = 'none';
  });

  // Detail modal close
  document.getElementById('close-detail-modal')!.addEventListener('click', () => {
    document.getElementById('detail-modal')!.style.display = 'none';
  });

  await loadBusinesses(navigate);
}

async function loadBusinesses(navigate: NavigateFn): Promise<void> {
  const listEl = document.getElementById('business-list')!;
  const statusFilter = (document.getElementById('filter-status') as HTMLSelectElement).value;
  const industryFilter = (document.getElementById('filter-industry') as HTMLSelectElement).value;
  const searchQ = ((document.getElementById('biz-search') as HTMLInputElement)?.value ?? '').toLowerCase();

  let all: Business[] = [];
  try {
    all = await getAPI().getAllBusinesses() as Business[];
  } catch {
    all = getSampleBusinesses();
  }

  updateStats(all);
  renderRevenueChart(all);

  const filtered = all.filter((b) => {
    const statusOk = statusFilter === 'all'
      ? true
      : statusFilter === 'paused' ? b.isPaused
      : b.status === statusFilter && !b.isPaused;
    const industryOk = industryFilter === 'all' || b.industry === industryFilter;
    const searchOk = !searchQ || b.name.toLowerCase().includes(searchQ) || b.email.toLowerCase().includes(searchQ);
    return statusOk && industryOk && searchOk;
  });

  if (filtered.length === 0) {
    listEl.innerHTML = `<div class="empty-state"><div class="empty-state-icon"><i class="bi bi-buildings" style="font-size:40px"></i></div><div class="empty-state-title">No businesses found</div></div>`;
    return;
  }

  const industryIcon: Record<string, string> = { construction: 'bi-building', restaurant: 'bi-cup-hot', general: 'bi-briefcase' };
  const industryColor: Record<string, string> = { construction: '#f59e0b', restaurant: '#ef4444', general: '#3b82f6' };

  listEl.innerHTML = filtered.map((b) => {
    const est = ESTIMATED_VALUE[b.industry] ?? ESTIMATED_VALUE['general']!;
    const isPaused = b.isPaused;
    const statusLabel = isPaused ? 'paused' : b.status;
    const statusBg = isPaused ? '#f59e0b22' : b.status === 'approved' ? '#10b98122' : b.status === 'pending' ? '#f59e0b22' : '#ef444422';
    const statusColor = isPaused ? '#f59e0b' : b.status === 'approved' ? '#10b981' : b.status === 'pending' ? '#f59e0b' : '#ef4444';
    const memberDays = Math.floor((Date.now() - new Date(b.createdAt).getTime()) / 86400000);

    return `
      <div class="admin-biz-row" data-id="${b.id}" style="position:relative;overflow:visible">
        <div style="display:flex;align-items:center;gap:14px;flex:1;min-width:0">
          <div style="width:40px;height:40px;border-radius:var(--radius-sm);background:${industryColor[b.industry] ?? '#64748b'}22;display:flex;align-items:center;justify-content:center;flex-shrink:0">
            <i class="bi ${industryIcon[b.industry] ?? 'bi-briefcase'}" style="font-size:20px;color:${industryColor[b.industry] ?? '#64748b'}"></i>
          </div>
          <div style="min-width:0;flex:1">
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
              <span style="font-weight:700;font-size:14px">${escHtml(b.name)}</span>
              <span style="background:${statusBg};color:${statusColor};padding:2px 8px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase">${statusLabel}</span>
              ${isPaused ? `<span style="font-size:10px;color:var(--warning)"><i class="bi bi-pause-circle"></i> ${escHtml(b.pauseReason ?? 'Paused')}</span>` : ''}
            </div>
            <div style="font-size:11px;color:var(--text-muted);margin-top:3px">
              <i class="bi bi-envelope"></i> ${escHtml(b.email)} &nbsp;·&nbsp;
              <i class="bi bi-telephone"></i> ${escHtml(b.phone)} &nbsp;·&nbsp;
              <i class="bi bi-geo-alt"></i> ${escHtml(b.address)}
            </div>
            ${b.status === 'approved' ? `
              <div style="font-size:11px;color:var(--text-muted);margin-top:2px">
                <i class="bi bi-calendar-check" style="color:var(--success)"></i> Member ${memberDays}d &nbsp;·&nbsp;
                <i class="bi bi-cash-coin" style="color:var(--brand-gold)"></i> Est. $${est.revenue}/mo &nbsp;·&nbsp;
                <i class="bi bi-piggy-bank" style="color:#3b82f6"></i> Saves ~$${est.saving.toLocaleString()}/mo
              </div>
            ` : ''}
          </div>
        </div>

        <!-- Action buttons -->
        <div style="display:flex;align-items:center;gap:6px;flex-shrink:0">
          <button class="btn btn-ghost btn-sm" data-action="detail" data-id="${b.id}" title="View details">
            <i class="bi bi-eye"></i>
          </button>
          ${b.status === 'approved' && !isPaused ? `
            <button class="btn btn-ghost btn-sm" data-action="copy-id" data-id="${b.id}" title="Copy Business ID">
              <i class="bi bi-clipboard"></i>
            </button>
            <button class="btn btn-ghost btn-sm" data-action="pause" data-id="${b.id}" data-name="${escHtml(b.name)}" title="Place account on hold" style="color:var(--warning)">
              <i class="bi bi-shield-exclamation"></i>
            </button>
          ` : ''}
          ${isPaused ? (() => {
            const holdRec = getHoldRecord(b.id);
            return `
              ${holdRec?.status === 'responded' ? `
                <button class="btn btn-sm hold-responded-badge" data-action="view-response" data-id="${b.id}" title="View hold response">
                  <i class="bi bi-eye"></i> Response
                </button>
              ` : ''}
              <button class="btn btn-sm" style="background:var(--success);color:#fff" data-action="resume" data-id="${b.id}">
                <i class="bi bi-play-circle"></i> Lift Hold
              </button>
            `;
          })() : ''}
          ${b.status === 'pending' ? `
            <button class="btn btn-sm" style="background:var(--success);color:#fff" data-action="approve" data-id="${b.id}">
              <i class="bi bi-check-lg"></i> Approve
            </button>
            <button class="btn btn-ghost btn-sm" data-action="reject" data-id="${b.id}" style="color:var(--danger)">
              <i class="bi bi-x-lg"></i> Reject
            </button>
          ` : ''}
          ${b.status === 'rejected' ? `
            <button class="btn btn-sm" style="background:var(--success);color:#fff" data-action="approve" data-id="${b.id}">
              <i class="bi bi-arrow-clockwise"></i> Re-Approve
            </button>
          ` : ''}
          <button class="btn btn-ghost btn-sm" data-action="delete" data-id="${b.id}" title="Delete business" style="color:var(--danger)">
            <i class="bi bi-trash3"></i>
          </button>
        </div>
      </div>
    `;
  }).join('');

  listEl.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const action = btn.getAttribute('data-action')!;
      const id = btn.getAttribute('data-id')!;
      await handleAction(action, id, all, navigate);
    });
  });
}

function updateStats(all: Business[]): void {
  const approved = all.filter((b) => b.status === 'approved' && !b.isPaused);
  const pending = all.filter((b) => b.status === 'pending');
  const mrr = approved.reduce((s, b) => s + (ESTIMATED_VALUE[b.industry]?.revenue ?? 79), 0);
  const savings = approved.reduce((s, b) => s + (ESTIMATED_VALUE[b.industry]?.saving ?? 800), 0);

  document.getElementById('stat-pending')!.textContent = String(pending.length);
  document.getElementById('stat-approved')!.textContent = String(approved.length);
  document.getElementById('stat-mrr')!.textContent = `$${mrr.toLocaleString()}`;
  document.getElementById('stat-savings')!.textContent = `$${savings.toLocaleString()}`;
}

function renderRevenueChart(all: Business[]): void {
  const chart = document.getElementById('revenue-chart')!;
  const approved = all.filter((b) => b.status === 'approved' && !b.isPaused);
  const byIndustry: Record<string, number> = {};
  for (const b of approved) {
    byIndustry[b.industry] = (byIndustry[b.industry] ?? 0) + (ESTIMATED_VALUE[b.industry]?.revenue ?? 79);
  }
  const industryColor: Record<string, string> = { construction: '#f59e0b', restaurant: '#ef4444', general: '#3b82f6' };
  const maxVal = Math.max(...Object.values(byIndustry), 1);

  chart.innerHTML = Object.entries(byIndustry).length === 0
    ? `<div style="color:var(--text-muted);font-size:13px;text-align:center;padding:20px">No active businesses yet.</div>`
    : Object.entries(byIndustry).map(([industry, rev]) => {
        const count = approved.filter((b) => b.industry === industry).length;
        return `
          <div>
            <div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:4px">
              <span style="color:var(--text-secondary);text-transform:capitalize"><i class="bi bi-circle-fill" style="color:${industryColor[industry] ?? '#64748b'};font-size:8px"></i> ${industry} (${count})</span>
              <span style="font-weight:700;color:var(--brand-gold)">$${rev}/mo</span>
            </div>
            <div style="background:var(--bg-tertiary);border-radius:4px;height:8px">
              <div style="height:100%;background:${industryColor[industry] ?? '#64748b'};width:${(rev / maxVal) * 100}%;border-radius:4px;transition:width .4s"></div>
            </div>
          </div>
        `;
      }).join('');
}

async function handleAction(action: string, id: string, businesses: Business[], navigate: NavigateFn): Promise<void> {
  const api = getAPI();

  if (action === 'detail') {
    const biz = businesses.find((b) => b.id === id);
    if (!biz) return;
    showDetailModal(biz);
    return;
  }

  if (action === 'approve') {
    try { await api.approveBusiness(id); } catch { /* preview */ }
    showToast('Business approved', 'success');
    await loadBusinesses(navigate);
  } else if (action === 'reject') {
    const reason = prompt('Rejection reason (shown to applicant):') ?? '';
    try { await api.rejectBusiness(id, reason); } catch { /* preview */ }
    showToast('Business rejected', 'info');
    await loadBusinesses(navigate);
  } else if (action === 'copy-id') {
    await navigator.clipboard.writeText(id);
    showToast('Business ID copied', 'success');
  } else if (action === 'pause') {
    const btn = document.querySelector(`[data-action="pause"][data-id="${id}"]`) as HTMLElement;
    const bizName = btn?.dataset['name'] ?? '';
    (document.getElementById('pause-biz-id') as HTMLInputElement).value = id;
    (document.getElementById('pause-biz-name') as HTMLInputElement).value = bizName;
    (document.getElementById('pause-reason') as HTMLTextAreaElement).value = '';
    (document.getElementById('pause-deadline') as HTMLInputElement).value = '';
    (document.getElementById('pause-admin-note') as HTMLInputElement).value = '';
    document.querySelectorAll<HTMLInputElement>('.hold-modal-req-cb').forEach(cb => { cb.checked = false; });
    document.getElementById('pause-modal')!.style.display = 'flex';
  } else if (action === 'view-response') {
    const holdRec = getHoldRecord(id);
    if (!holdRec?.response) { showToast('No response on file', 'info'); return; }
    const resp = holdRec.response;
    const { ALL_REQUIREMENTS: allReqs } = await import('./AccountHold.js');
    document.getElementById('hold-resp-title')!.textContent = `Hold Response — ${escHtml(holdRec.businessName)}`;
    document.getElementById('hold-resp-content')!.innerHTML = `
      <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px">
        Submitted: <strong>${new Date(resp.submittedAt).toLocaleString()}</strong>
      </p>
      ${resp.offerings.length > 0 ? `
        <h4 style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:var(--text-muted);margin-bottom:10px">Commitments Made</h4>
        <ul style="list-style:none;padding:0;margin:0 0 16px;display:flex;flex-direction:column;gap:6px">
          ${resp.offerings.map(k => {
            const r = allReqs.find(rr => rr.key === k);
            return `<li style="font-size:13px;color:var(--text-secondary)"><i class="bi bi-check2-circle" style="color:var(--success);margin-right:6px"></i>${escHtml(r?.label ?? k)}</li>`;
          }).join('')}
        </ul>
      ` : ''}
      ${resp.message ? `
        <h4 style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:var(--text-muted);margin-bottom:10px">Message</h4>
        <blockquote style="background:var(--bg-tertiary);border-left:3px solid var(--brand-gold);padding:10px 14px;border-radius:0 6px 6px 0;font-size:13px;color:var(--text-secondary);line-height:1.6;margin:0 0 16px">${escHtml(resp.message)}</blockquote>
      ` : ''}
      ${resp.documents.length > 0 ? `
        <h4 style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;color:var(--text-muted);margin-bottom:10px">Uploaded Documents (${resp.documents.length})</h4>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:10px;margin-bottom:16px">
          ${resp.documents.map(d => `
            <div style="background:var(--bg-primary);border:1px solid var(--border);border-radius:8px;overflow:hidden">
              <div style="height:100px;display:flex;align-items:center;justify-content:center;overflow:hidden;background:var(--bg-secondary)">
                ${d.mimeType.startsWith('image/')
                  ? `<img src="${d.dataUri}" style="width:100%;height:100%;object-fit:cover" alt="${escHtml(d.fileName)}" />`
                  : `<i class="bi bi-file-earmark-pdf-fill" style="font-size:40px;color:var(--danger)"></i>`}
              </div>
              <div style="padding:8px;font-size:11px;color:var(--text-muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${escHtml(d.fileName)}</div>
            </div>`).join('')}
        </div>
      ` : ''}
      <div style="display:flex;justify-content:flex-end;gap:10px">
        <button class="btn btn-ghost btn-sm" id="hold-resp-keep">Keep on Hold</button>
        <button class="btn btn-sm" style="background:var(--success);color:#fff" id="hold-resp-lift" data-id="${holdRec.businessId}">
          <i class="bi bi-unlock"></i> Lift Hold
        </button>
      </div>
    `;
    document.getElementById('hold-response-modal')!.style.display = 'flex';
    document.getElementById('hold-resp-keep')?.addEventListener('click', () => {
      document.getElementById('hold-response-modal')!.style.display = 'none';
    });
    document.getElementById('hold-resp-lift')?.addEventListener('click', async () => {
      liftHold(id);
      try { await api.resumeBusiness(id); } catch { /* preview */ }
      showToast('Hold lifted — account reinstated', 'success');
      document.getElementById('hold-response-modal')!.style.display = 'none';
      await loadBusinesses(navigate);
    });
  } else if (action === 'resume') {
    liftHold(id);
    try { await api.resumeBusiness(id); } catch { /* preview */ }
    showToast('Hold lifted — account reinstated', 'success');
    await loadBusinesses(navigate);
  } else if (action === 'delete') {
    const biz = businesses.find((b) => b.id === id);
    if (!confirm(`Permanently delete "${biz?.name}"? This cannot be undone.`)) return;
    try { await api.deleteBusiness(id); } catch { /* preview */ }
    showToast('Business deleted', 'info');
    await loadBusinesses(navigate);
  }
}

function showDetailModal(biz: Business): void {
  const est = ESTIMATED_VALUE[biz.industry] ?? ESTIMATED_VALUE['general']!;
  const memberDays = Math.floor((Date.now() - new Date(biz.createdAt).getTime()) / 86400000);
  const industryIcon: Record<string, string> = { construction: 'bi-building', restaurant: 'bi-cup-hot', general: 'bi-briefcase' };

  document.getElementById('detail-title')!.textContent = biz.name;
  document.getElementById('detail-content')!.innerHTML = `
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px">
      <div class="stat-card" style="padding:12px">
        <div style="font-size:18px;font-weight:800;color:var(--brand-gold)">$${est.revenue}/mo</div>
        <div style="font-size:10px;color:var(--text-muted)">Est. Revenue</div>
      </div>
      <div class="stat-card" style="padding:12px">
        <div style="font-size:18px;font-weight:800;color:#3b82f6">~$${est.saving.toLocaleString()}/mo</div>
        <div style="font-size:10px;color:var(--text-muted)">Client Savings</div>
      </div>
      <div class="stat-card" style="padding:12px">
        <div style="font-size:18px;font-weight:800">${memberDays}d</div>
        <div style="font-size:10px;color:var(--text-muted)">Member Duration</div>
      </div>
      <div class="stat-card" style="padding:12px">
        <div style="font-size:18px;font-weight:800;text-transform:capitalize">${biz.industry}</div>
        <div style="font-size:10px;color:var(--text-muted)">Industry</div>
      </div>
    </div>
    <div style="display:flex;flex-direction:column;gap:8px;font-size:13px">
      ${[
        ['<i class="bi bi-envelope"></i> Email', biz.email],
        ['<i class="bi bi-telephone"></i> Phone', biz.phone],
        ['<i class="bi bi-geo-alt"></i> Location', biz.address],
        ['<i class="bi bi-geo-fill"></i> State', biz.state ?? 'Not specified'],
        ['<i class="bi bi-calendar-plus"></i> Applied', new Date(biz.createdAt).toLocaleDateString()],
        biz.approvedAt ? ['<i class="bi bi-calendar-check"></i> Approved', new Date(biz.approvedAt).toLocaleDateString()] : null,
        ['<i class="bi bi-hash"></i> Business ID', biz.id],
        biz.description ? ['<i class="bi bi-card-text"></i> Description', biz.description] : null,
      ].filter((x): x is string[] => x !== null).map(([label, val]) => `
        <div style="display:flex;gap:12px;padding:8px;background:var(--bg-tertiary);border-radius:var(--radius-sm)">
          <span style="color:var(--text-muted);min-width:130px;flex-shrink:0">${label}</span>
          <span style="color:var(--text-primary);font-weight:500;word-break:break-all">${escHtml(String(val))}</span>
        </div>
      `).join('')}
    </div>
  `;
  document.getElementById('detail-modal')!.style.display = 'flex';
}

async function sendInvitation(): Promise<void> {
  const name = (document.getElementById('inv-name') as HTMLInputElement).value.trim();
  const email = (document.getElementById('inv-email') as HTMLInputElement).value.trim();
  const biz = (document.getElementById('inv-biz') as HTMLInputElement).value.trim();
  const industry = (document.getElementById('inv-industry') as HTMLSelectElement).value;
  const message = (document.getElementById('inv-message') as HTMLTextAreaElement).value.trim();

  if (!name || !email) { showToast('Name and email are required', 'error'); return; }

  const btn = document.getElementById('send-invite-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<span class="spinner"></span> Sending…';

  // Call backend SendGrid handler
  try {
    await getAPI().sendInvitationEmail({ name, email, businessName: biz, industry, personalMessage: message });
    showToast(`Invitation sent to ${email}`, 'success');
  } catch {
    // Preview mode — simulate success
    showToast(`Invitation queued for ${email} (configure SENDGRID_API_KEY to send)`, 'info');
  }

  btn.disabled = false;
  btn.innerHTML = '<i class="bi bi-send"></i> Send Invitation';
  document.getElementById('invite-modal')!.style.display = 'none';
}

function getSampleBusinesses(): Business[] {
  const now = new Date();
  const ago = (days: number) => new Date(now.getTime() - days * 86400000).toISOString();
  return [
    { id: 'sb1', name: 'Apex Construction LLC', industry: 'construction', email: 'apex@example.com', phone: '(206) 555-0101', address: 'Seattle, WA', description: 'Commercial general contractor', status: 'approved', createdAt: ago(90), approvedAt: ago(88), ownerId: 'o1', state: 'WA' },
    { id: 'sb2', name: 'The Golden Fork', industry: 'restaurant', email: 'fork@example.com', phone: '(206) 555-0102', address: 'Bellevue, WA', description: 'Fine dining restaurant', status: 'approved', createdAt: ago(60), approvedAt: ago(58), ownerId: 'o2', state: 'WA' },
    { id: 'sb3', name: 'Summit Solutions', industry: 'general', email: 'summit@example.com', phone: '(206) 555-0103', address: 'Tacoma, WA', description: 'IT consulting', status: 'approved', createdAt: ago(45), approvedAt: ago(44), ownerId: 'o3', state: 'WA' },
    { id: 'sb4', name: 'Pacific Builders Inc', industry: 'construction', email: 'pacific@example.com', phone: '(360) 555-0104', address: 'Olympia, WA', description: 'Residential contractor', status: 'pending', createdAt: ago(3), ownerId: 'o4', state: 'WA' },
    { id: 'sb5', name: 'Sunrise Bistro', industry: 'restaurant', email: 'sunrise@example.com', phone: '(425) 555-0105', address: 'Kirkland, WA', description: 'Breakfast & brunch cafe', status: 'pending', createdAt: ago(1), ownerId: 'o5', state: 'WA' },
    { id: 'sb6', name: 'Harbor Legal Group', industry: 'general', email: 'harbor@example.com', phone: '(206) 555-0106', address: 'Seattle, WA', description: 'Legal services', status: 'approved', createdAt: ago(120), approvedAt: ago(119), ownerId: 'o6', state: 'WA', isPaused: true, pauseReason: 'Outstanding invoice — 45 days past due' },
    { id: 'sb7', name: 'Cascade Electric', industry: 'construction', email: 'cascade@example.com', phone: '(206) 555-0107', address: 'Renton, WA', description: 'Electrical contractor', status: 'approved', createdAt: ago(200), approvedAt: ago(198), ownerId: 'o7', state: 'WA' },
  ];
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
